// @flow

import React, { Fragment } from 'react';

import { AppsOverview } from '@zapier/design-system-app-profiles';
import { Screen } from '@zapier/theme';
import { useMediaProps } from '@zapier/common-components';
import { Colors, Spacer } from '@zapier/design-system';

import ApolloQuery from 'web-ssr/app/common/components/ApolloQuery';
import appsOverviewQuery from 'web-ssr/app/app-directory/components/AppsOverview/appsOverviewQuery';
import { createAppIntegrationsPageLink } from 'web-ssr/app/app-directory/utils';

import type {
  appsOverviewQuery as appsOverviewQueryType,
  appsOverviewQueryVariables,
} from 'web-ssr/graphql/types';

type Result = {
  loading: *,
  fetchMore: *,
  error: *,
  data: appsOverviewQueryType,
};

type Props = {
  /** Renders an additional top spacer on mobile and tablet views. */
  renderTopSpacer: boolean,
  /** Renders an additional bottom spacer on mobile and tablet views. */
  renderBottomSpacer: boolean,
  slugs: Array<string>,
};

const AppsOverviewContainer = (props: Props) => {
  const { slugs, renderTopSpacer, renderBottomSpacer } = props;

  const variables: appsOverviewQueryVariables = { slugs };
  const layoutProps = useMediaProps({
    [Screen.queries.phone]: {
      mode: 'mobile',
      topSpacer: '15px',
      bottomSpacer: '15px',
    },
    [Screen.queries.phablet]: {
      mode: 'mobile',
      topSpacer: '15px',
      bottomSpacer: '15px',
    },
    [Screen.queries.tablet]: {
      mode: 'header',
      topSpacer: '15px',
      bottomSpacer: '15px',
    },
    [Screen.queries.desktop]: {
      mode: 'sidebar',
      topSpacer: '0',
      bottomSpacer: '0',
    },
  });

  return (
    <ApolloQuery query={appsOverviewQuery} variables={variables}>
      {(result: Result) => {
        const { data, error } = result;
        const { appsBySlug } = data || { appsBySlug: [] };

        if (error || !appsBySlug || appsBySlug.length === 0) {
          return null;
        }

        const apps = appsBySlug.reduce((acc, app) => {
          if (!app) {
            return acc;
          }

          acc.push({
            name: app.name,
            categories: app.categories.map(c => c.name),
            color: Colors.grey000,
            description: app.description,
            iconSrc: app.image || '',
            detailsHref: createAppIntegrationsPageLink(app.slug),
          });

          return acc;
        }, []);

        return (
          <Fragment>
            {renderTopSpacer && <Spacer height={layoutProps.topSpacer} />}
            <AppsOverview apps={apps} mode={layoutProps.mode} />
            {renderBottomSpacer && <Spacer height={layoutProps.bottomSpacer} />}
          </Fragment>
        );
      }}
    </ApolloQuery>
  );
};

AppsOverviewContainer.defaultProps = {
  renderTopSpacer: true,
  renderBottomSpacer: true,
};

export default AppsOverviewContainer;
